import React from 'react'
import styled, { css } from 'styled-components'
import { node, bool, string } from 'prop-types'
import { rgba, stripUnit } from 'polished'

import { media } from 'styles/media'
import { fontWeights } from 'styles/typography'
import { buttonBackColor, disabledButtonBackColor, disabledTextColor } from 'styles/colors'
import { animationTime, animationCurve, inputHeight, borderRadius } from 'styles/global'
import * as spacing from 'styles/spacing'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'

export const Container = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border-radius: ${borderRadius};
  padding: 0 ${stripUnit(spacing.medium) * 1.25 + 'px'};
  cursor: pointer;
  text-align: center;
  transition: all ${animationTime} ${animationCurve};
  text-transform: uppercase;

  ${media.medium`
    padding: 0 ${spacing.large};
  `};

  ${media.large`
    padding: 0 ${spacing.xLarge};
  `};

  ${props =>
    props.fullwidth &&
    css`
      display: flex;
      width: 100%;
    `}

  ${props =>
    props.theme === 'primary' &&
    css`
      background-color: ${buttonBackColor};

      &:hover,
      &:active {
        background-color: ${rgba(buttonBackColor, 0.6)};
      }
    `}

  ${props =>
    props.size === 'small' &&
    css`
      height: ${stripUnit(inputHeight) * 0.875 + 'px'};
      padding: 0 ${spacing.medium};

      ${media.medium`
        padding: 0 ${stripUnit(spacing.medium) * 1.25 + 'px'};
      `};

      ${media.large`
        padding: 0 ${spacing.large};
      `};
    `}

  &[disabled] {
    cursor: default;
    pointer-events: none;
    user-select: none;
    color: ${disabledTextColor};
    background-color: ${disabledButtonBackColor};
  }
`

const StyledIcon = styled(Icon)`
  margin-left: ${spacing.mini};
  flex-shrink: 0;
`

const Label = styled.span`
  display: flex;
  font-weight: ${fontWeights.bold};
  align-items: center;
  transition: opacity ${animationTime} ${animationCurve};

  ${props =>
    props.isLoading &&
    css`
      opacity: 0;
    `};
`

const getElement = props => {
  if (props.href) return 'a'
  return 'button'
}

const Button = ({ children, theme, size, icon, disabled, isLoading, fullwidth, type = 'button', ...rest }) => (
  <Container
    fullwidth={fullwidth}
    theme={theme}
    size={size}
    isLoading={isLoading}
    disabled={disabled || isLoading}
    as={getElement(rest)}
    type={type}
    {...rest}
  >
    {isLoading && <Spinner position="center" />}
    <Label isLoading={isLoading}>
      {children}
      {icon && <StyledIcon icon={icon} />}
    </Label>
  </Container>
)

Button.propTypes = {
  children: node.isRequired,
  theme: string,
  size: string,
  fullwidth: bool,
  disabled: bool,
  isLoading: bool,
  icon: string,
  type: string
}

export default Button
